import type { FlowDefinition, FlowFieldDefinition, FlowNode } from '../types';
import { REQUEST_FLOW_FIELDS, REQUEST_FLOW_FIELD_IDS } from './flowFieldExtensions';
import { resolveOrCreateSectionInsertionFlow } from './sectionInsertionFlow';

function getRequestFormRemovedItemFieldId(blockId: string) {
  return `request-form-removed-item-${blockId}`;
}

function getRequestFormSubmitSuccessFieldId(blockId: string) {
  return `request-form-submit-success-${blockId}`;
}

function buildRequestFormRemovedItemField(blockId: string): FlowFieldDefinition {
  return {
    id: getRequestFormRemovedItemFieldId(blockId),
    label: 'Removed Item',
    type: 'scalar.string',
  };
}

function buildRequestFormSubmitSuccessField(blockId: string): FlowFieldDefinition {
  return {
    id: getRequestFormSubmitSuccessFieldId(blockId),
    label: 'Submit Success',
    type: 'event.trigger',
  };
}

function upsertFields(fields: FlowFieldDefinition[], additions: FlowFieldDefinition[]) {
  const nextById = new Map(fields.map((field) => [field.id, field]));
  additions.forEach((field) => {
    if (!nextById.has(field.id)) {
      nextById.set(field.id, field);
    }
  });
  return Array.from(nextById.values());
}

function upsertNodes(nodes: FlowNode[], additions: FlowNode[]) {
  const nextByBlockId = new Map(nodes.map((node) => [node.blockId, node]));
  additions.forEach((node) => {
    const existing = nextByBlockId.get(node.blockId);
    if (!existing) {
      nextByBlockId.set(node.blockId, node);
      return;
    }

    const mergeBindings = (
      current: FlowNode['inputs'] | FlowNode['outputs'],
      defaults: FlowNode['inputs'] | FlowNode['outputs']
    ) => {
      const next = [...current];
      defaults.forEach((binding) => {
        if (!next.some((entry) => entry.socketId === binding.socketId)) {
          next.push(binding);
        }
      });
      return next;
    };

    nextByBlockId.set(node.blockId, {
      ...existing,
      id: existing.id || node.id,
      blockType: node.blockType,
      config: { ...(node.config ?? {}), ...(existing.config ?? {}) },
      inputs: mergeBindings(existing.inputs, node.inputs),
      outputs: mergeBindings(existing.outputs, node.outputs),
    });
  });
  return Array.from(nextByBlockId.values());
}

export function upsertRequestFormFlowNode(
  flows: FlowDefinition[],
  args: {
    sectionId: string;
    blockId: string;
    sections?: Array<{
      id: string;
      settings?: {
        canvas?: {
          blocks?: Array<{
            id: string;
            type: string;
          }>;
        };
      };
    }> | null;
  }
) {
  const { flows: nextFlows, flow: targetFlow } = resolveOrCreateSectionInsertionFlow(flows, {
    sectionId: args.sectionId,
    sections: args.sections,
  });

  if (!targetFlow) return nextFlows;

  const nextNode: FlowNode = {
    id: `flow-node-${args.blockId}`,
    blockId: args.blockId,
    blockType: 'request-form',
    inputs: [],
    outputs: [
      { socketId: 'submitSuccess', fieldId: getRequestFormSubmitSuccessFieldId(args.blockId) },
      { socketId: 'removedItemId', fieldId: getRequestFormRemovedItemFieldId(args.blockId) },
    ],
  };

  const updatedFlow: FlowDefinition = {
    ...targetFlow,
    fields: upsertFields(targetFlow.fields, [
      ...REQUEST_FLOW_FIELDS,
      buildRequestFormSubmitSuccessField(args.blockId),
      buildRequestFormRemovedItemField(args.blockId),
    ]),
    nodes: upsertNodes(targetFlow.nodes, [nextNode]),
  };

  return nextFlows.map((flow) => (flow.id === updatedFlow.id ? updatedFlow : flow));
}

export function upsertRequestFormFlowNodesForSections(
  flows: FlowDefinition[],
  sections: Array<{
    id: string;
    settings?: {
      canvas?: {
        blocks?: Array<{
          id: string;
          type: string;
        }>;
      };
    };
  }>
) {
  return sections.reduce((nextFlows, section) => {
    const requestForms = section.settings?.canvas?.blocks?.filter(
      (block) => block.type === 'request-form'
    );
    if (!requestForms || requestForms.length === 0) return nextFlows;
    return requestForms.reduce(
      (currentFlows, block) =>
        upsertRequestFormFlowNode(currentFlows, {
          sectionId: section.id,
          blockId: block.id,
          sections,
        }),
      nextFlows
    );
  }, flows);
}
